import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconClock, IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { MultiSelectWithSearch } from "@components/MultiSelectWithSearch";
import { PermissionGate } from "@components/PermissionGate";
import { RejectAppointmentModal } from "@components/RejectAppointmentModal";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";
import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import { MAX_FILENAME_LENGTH_AFTER_UPLOAD, MEGABYTE } from "@constants/index";

import { useUpdateAppointment } from "@hooks/mutations/appointment/useUpdateAppointment";
import { useGetClinicLabs } from "@hooks/queries/clinic/useGetClinicLabs";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { useAuth } from "@contexts/auth";

import {
  Appointment,
  LabAppointmentDetail,
  UpdateIncomingAppointment,
} from "@customTypes/appointment";
import { BasicUser } from "@customTypes/user";

import { EditTeleconsultationAppointmentDialogProps } from "../EditTeleconsultationAppointmentModal";

import { StyledPrescriptionDocuments } from "./EditLabAppointmentModal.styled";


const MAX_FILENAME_LENGTH = MAX_FILENAME_LENGTH_AFTER_UPLOAD + 25;

export const EditLabAppointmentModal = ({
  selectedRow,
  doctors,
  variant,
  opened,
  close,
}: EditTeleconsultationAppointmentDialogProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [appointmentDate, setAppointmentDate] = useState<Date | null>(null);
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [formData, setFormData] = useState<Partial<Appointment>>({});
  const [rejectModalOpened, setRejectModalOpened] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [labFiles, setLabFiles] = useState<string[]>([]);
  const [labTypes, setLabTypes] = useState<ComboboxItem[]>([]);

  const timePickerRef = useRef<HTMLInputElement>(null);

  const { mutate: updateAppointment, isLoading: isUpdateAppointmentLoading } =
    useUpdateAppointment();
  const { data: clinicLabsData } = useGetClinicLabs();
  const handleErrors = useQueryErrorHandling();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleUpdateAppointment = () => {
    const jsFormData = new FormData();
    if (uploadedFiles?.length) {
      uploadedFiles.forEach((file: File) => {
        jsFormData.append(`files`, file);
      });
    }
    jsFormData.append("doctor_assigned", doctor?.value ?? "");
    jsFormData.append("status", formData?.status ?? "");
    jsFormData.append("appointment_date", appointmentDate?.toISOString() ?? "");
    jsFormData.append(
      "details",
      JSON.stringify({ service_date: appointmentDate?.toISOString() ?? "" })
    );
    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      import.meta.env.DEV && console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    const id = showLoadingNotification({});
    updateAppointment(
      {
        appointmentId: formData.id as string,
        formData: jsFormData,
      },
      {
        onSuccess: () => {
          showSuccessNotification({
            id,
            title: "success",
            message: "update-appointment-success",
          });
          close();
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      }
    );
  };

  useEffect(() => {
    if (selectedRow) {
      const {
        details,
        id,
        category,
        doctorAssigned,
        status,
        paymentTransaction,
        appointmentDate,
        medium,
        promoCode,
        files,
      } = selectedRow;

      setDoctor({
        label: (doctorAssigned as BasicUser)?.fullName,
        value: (doctorAssigned as BasicUser)?.id,
      });
      setAppointmentDate(new Date(appointmentDate));
      setFormData({
        id,
        status,
        doctorAssigned,
        category,
        details,
        paymentTransaction,
        medium,
        promoCode,
      });
      setLabFiles(files as string[]);
      setLabTypes(
        (details as LabAppointmentDetail).serviceTypeIds as ComboboxItem[]
      );
    }
  }, [selectedRow]);

  useEffect(() => {
    if (appointmentDate) {
      setFormData(prevData => ({ ...prevData, date: appointmentDate }));
    }
  }, [appointmentDate]);

  return (
    <>
    <Modal
      centered
      size={"lg"}
      title={<Text fw={500}>{t("edit-analysis-appointment")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}
      styles={{ body: { minHeight: "auto" } }}>
      <Tabs defaultValue={"contact"}>
        <Tabs.List grow>
          <Tabs.Tab value="contact">{t("contact")}</Tabs.Tab>
          <Tabs.Tab value="appointment">{t("appointment")}</Tabs.Tab>
          <Tabs.Tab value="prescription">
            {t("prescription-documents")}
          </Tabs.Tab>
          <Tabs.Tab value="billing">{t("billing")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="contact">
          <form className="form">
            <div className="form-group">
              <TextInput
                label={t("name")}
                disabled
                value={
                  (formData.details as LabAppointmentDetail)?.firstName +
                  " " +
                  (formData.details as LabAppointmentDetail)?.surname
                }
              />
              <TextInput
                label={t("phone-number")}
                disabled
                value={
                  (formData.details as LabAppointmentDetail)?.phoneNumber || ""
                }
              />
            </div>

            <div className="form-group">
              <TextInput
                label={t("gender")}
                disabled
                value={(formData.details as LabAppointmentDetail)?.gender || ""}
              />
              <TextInput
                label={t("location")}
                disabled
                value={(formData.details as LabAppointmentDetail)?.city || ""}
              />
            </div>
            <div className="form-group">
              <TextInput
                label={t("medium")}
                disabled
                value={formData?.medium || ""}
              />
            </div>
          </form>
        </Tabs.Panel>

        <Tabs.Panel value="appointment">
          <form className="form">
            <div className="form-group">
              <MultiSelectWithSearch
                label={t("lab-type")}
                placeholder={t("select-lab-types")}
                searchPlaceholder={t("search-lab-type")}
                withAsterisk
                value={labTypes}
                setValue={setLabTypes}
                data={clinicLabsData ?? []}
                withinPortal
              />
              <DateTimePicker
                label={t("date-and-time")}
                value={appointmentDate}
                onChange={setAppointmentDate}
                timeInputProps={{
                  ref: timePickerRef,
                  leftSection: (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => timePickerRef?.current?.showPicker()}>
                      <IconClock size={16} />
                    </ActionIcon>
                  ),
                  // minTime: "08:00:00",
                  // maxTime: "17:00:00",
                }}
              />
            </div>
            <div className="form-group">
              <Select
                label={t("partner")}
                placeholder={t("select-partner")}
                required
                data={doctors}
                value={
                  doctor?.value ??
                  (selectedRow.doctorAssigned as BasicUser)?.id ??
                  null
                }
                onChange={(_val, option) => {
                  setDoctor(option);
                }}
              />
              <TextInput
                label={t("price") + " (CFA)"}
                disabled
                value={(formData.details as LabAppointmentDetail)?.cost || ""}
              />
            </div>
            <Textarea
              label={t("additional-health-info")}
              disabled
              value={
                (formData.details as LabAppointmentDetail)
                  ?.additionalHealthInfo || "N/A"
              }
              rows={3}
            />
          </form>
        </Tabs.Panel>

        <Tabs.Panel value="prescription">
          <StyledPrescriptionDocuments>
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} required name="dropzone" />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles?.length && !labFiles?.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize h={200} type="auto" mt="sm">
                    <ul>
                      {labFiles.reverse().map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            {(file as string).slice(-3) === "pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={"https://" + file + `?token=${token}`}>
                              {truncateWithEllipsis(
                                (file as string).split("/")[3],
                                MAX_FILENAME_LENGTH
                              )}
                            </a>
                          </div>

                          {/* <Tooltip label={t("delete-lab-file")} withArrow>
                            <ActionIcon
                              color="red"
                              loading={
                                isDeleteLabFileLoading && fileIndex === index
                              }
                              variant="outline"
                              onClick={() => {
                                setFileIndex(index);
                                openDeleteModal({
                                  textNode: (
                                    <Text fz={"sm"}>
                                      {t("delete-lab-file-message")}
                                    </Text>
                                  ),
                                  onConfirm: () => {
                                    if (lab?.id) {
                                      deleteLabFile(
                                        {
                                          labId: lab.id,
                                          fileName: (file as string).split(
                                            "/"
                                          )[3],
                                          patientId: lab.patientId as string,
                                        },
                                        {
                                          onSuccess: () =>
                                            setLabFiles(
                                              labFiles.filter(
                                                (_, idx) => idx !== index
                                              )
                                            ),
                                        }
                                      );
                                    }
                                  },
                                });
                              }}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip> */}
                        </li>
                      ))}
                    </ul>

                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            variant="outline"
                            color="red"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </StyledPrescriptionDocuments>
        </Tabs.Panel>

        <Tabs.Panel value="billing">
          <form className="form">
            <div className="form-group">
              <TextInput
                label={t("payment-method")}
                disabled
                value={
                  (formData.details as LabAppointmentDetail)?.paymentMethod ||
                  "N/A"
                }
              />
              <TextInput
                label={t("amount-paid")}
                disabled
                value={formData.paymentTransaction?.amountPaid || 0}
              />
            </div>

            <div className="form-group">
              <TextInput
                label={t("payer")}
                disabled
                value={
                  (formData.details as LabAppointmentDetail)?.insuranceType ||
                  "N/A"
                }
              />
              <TextInput
                label={t("policy-number")}
                disabled
                value={
                  (formData.details as LabAppointmentDetail)?.insuranceInfo ||
                  "N/A"
                }
              />
            </div>
            <div className="form-field">
              <TextInput
                w={"calc(50% - 0.8rem)"}
                label={t("promo-code")}
                disabled
                value={formData.promoCode || "N/A"}
              />
            </div>
          </form>
        </Tabs.Panel>

        <Group mt="xl" justify="end" pb="md">
          <PermissionGate permission={variant === "incoming" ? "appointments.assign" : "appointments.edit"}><Button
            variant="light"
            color="red"
            disabled={isUpdateAppointmentLoading}
            onClick={() => {
              if (variant === "incoming") {
                setRejectModalOpened(true);
                return;
              }
              const id = showLoadingNotification({});
              const jsFormData = new FormData();
              jsFormData.append("status", "cancelled");
              updateAppointment(
                {
                  appointmentId: formData.id as string,
                  formData: jsFormData,
                },
                {
                  onSuccess: () => {
                    close();
                    showSuccessNotification({
                      id,
                      title: "success",
                    message: "cancel-appointment-success",
                    });
                  },
                  onError: res => {
                    handleErrors(res, "mutation", id);
                  },
                }
              );
            }}>
            {variant === "incoming"
              ? t("reject-request")
              : t("cancel-appointment")}
          </Button></PermissionGate>
          <PermissionGate permission="appointments.edit"><Button
            onClick={handleUpdateAppointment}
            disabled={
              !appointmentDate ||
              (!(selectedRow.doctorAssigned as BasicUser)?.id && !doctor?.value)
            }
            loading={isUpdateAppointmentLoading}>
            {t("update")}
          </Button></PermissionGate>
        </Group>
      </Tabs>
    </Modal>
    <RejectAppointmentModal
      opened={rejectModalOpened}
      close={() => setRejectModalOpened(false)}
      appointmentId={formData.id as string}
      onRejected={close}
    />
    </>
  );
};
